import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Box, Typography, Breadcrumbs, Link, IconButton } from "@mui/material";
import React from "react";
import { useParams, useNavigate } from "react-router";

import { MetricChart } from "components/features/patient/detail";
import { DashboardLayout } from "components/layouts";
import { EPath } from "constants/path";
import { MetricValuesAggregationRequestDtoMetricCodeEnum } from "libs/cellera-sdk";
import { useCelleraTheme } from "theme/useCelleraTheme";

const NUTRITION_HYDRATION_METRICS = [
  {
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.Calories,
    title: "Nutrition",
  },
  {
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.Protein,
    title: "Protein",
  },
  {
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.Carbohydrates,
    title: "Carbohydrates",
  },
  {
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.TotalFat,
    title: "Total Fat",
  },
  {
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.SaturatedFat,
    title: "Saturated Fat",
  },
  {
    metricCode:
      MetricValuesAggregationRequestDtoMetricCodeEnum.MonounsaturatedFat,
    title: "Monounsaturated Fat",
  },
  {
    metricCode:
      MetricValuesAggregationRequestDtoMetricCodeEnum.PolyunsaturatedFat,
    title: "Polyunsaturated Fat",
  },
  {
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.Cholesterol,
    title: "Cholesterol",
  },
  {
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.Micronutrients,
    title: "Micronutrients",
  },
  {
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.Hydration,
    title: "Hydration",
  },
];

const NutritionHistoryPage: React.FC = () => {
  const { colors } = useCelleraTheme();
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();

  if (!patientId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Patient ID not found</Typography>
      </Box>
    );
  }

  return (
    <DashboardLayout title="Nutrition and Hydration">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 2,
          backgroundColor: colors.background.paper,
          minHeight: "100vh",
        }}
      >
        {/* Breadcrumbs with Back/Refresh buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Breadcrumbs>
            <Link
              color="inherit"
              component="button"
              onClick={() => navigate(EPath.PRACTICE_MANAGEMENT)}
            >
              Dashboard
            </Link>
            <Link
              color="inherit"
              component="button"
              onClick={() => navigate(EPath.PATIENT_MANAGEMENT)}
            >
              Patient Management
            </Link>
            <Link
              color="inherit"
              component="button"
              onClick={() =>
                navigate(EPath.PATIENT_DETAIL.replace(":patientId", patientId))
              }
            >
              Patient Details
            </Link>
            <Typography color="text.primary">
              Nutrition and Hydration
            </Typography>
          </Breadcrumbs>

          {/* Back and Refresh buttons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ color: "text.secondary" }}
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              onClick={() => window.location.reload()}
              sx={{ color: "text.secondary" }}
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 351px)",
              md: "repeat(3, 351px)",
            },
            gap: 2,
            width: "100%",
            justifyContent: "center",
          }}
        >
          {NUTRITION_HYDRATION_METRICS.map((metric) => (
            <MetricChart
              key={metric.metricCode}
              patientId={patientId}
              metricCode={metric.metricCode}
              title={metric.title}
            />
          ))}
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default NutritionHistoryPage;
